import type React from "react";
import { useMemo, useState } from "react";
import {
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

interface SpeedDataPoint {
  hour: string;
  speed: number;
  rpm?: number | string;
  is_stop?: boolean;
  stop_time?: string | number;
  label?: string;
  start_hour?: string;
  end_hour?: string;
}

interface SpeedChartProps {
  start_hour: string;
  end_hour: string;
  data: SpeedDataPoint[];
  warningThreshold?: number;
  dangerThreshold?: number;
  height?: number;
  isLoading?: boolean;
  hasCanTelemetry?: boolean;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    payload: SpeedDataPoint;
    dataKey?: string;
  }>;
  label?: string;
}

interface CustomDotProps {
  cx?: number;
  cy?: number;
  payload?: SpeedDataPoint;
}

interface Statistics {
  max: number;
  avg: number;
  exceedWarningCount: number;
  exceedDangerCount: number;
  stopCount: number;
  avgRpm: number;
  maxRpm: number;
}

interface ActiveDotProps {
  cx?: number;
  cy?: number;
  r?: number;
  stroke?: string;
  strokeWidth?: number;
  fill?: string;
  payload?: SpeedDataPoint;
  index?: number;
  onClick?: (event: React.MouseEvent) => void;
}

interface PopupData {
  isOpen: boolean;
  position: { x: number; y: number };
  data: SpeedDataPoint | null;
}

const SpeedChart: React.FC<SpeedChartProps> = ({
  data,
  warningThreshold = 80,
  dangerThreshold = 110,
  height = 300,
  hasCanTelemetry = false,
}) => {
  const [popup, setPopup] = useState<PopupData>({
    isOpen: false,
    position: { x: 0, y: 0 },
    data: null,
  });

  const handleDotClick = (dataPoint: SpeedDataPoint, x: number, y: number) => {
    setPopup({
      isOpen: true,
      position: { x, y },
      data: dataPoint,
    });
  };

  const closePopup = () => {
    setPopup((prev) => ({ ...prev, isOpen: false }));
  };

  const CustomActiveDot = (props: ActiveDotProps) => {
    if (!props.payload) return null;

    return (
      <circle
        cx={props.cx}
        cy={props.cy}
        r={props.r || 5}
        fill={props.fill || "#3b82f6"}
        stroke={props.stroke || "#fff"}
        strokeWidth={props.strokeWidth || 2}
        onClick={(e) => {
          if (props.payload) {
            handleDotClick(props.payload, props.cx || 0, props.cy || 0);
          }
          props.onClick?.(e);
        }}
        style={{ cursor: "pointer" }}
      />
    );
  };

  const statistics = useMemo((): Statistics => {
    if (!data.length)
      return {
        max: 0,
        avg: 0,
        exceedWarningCount: 0,
        exceedDangerCount: 0,
        stopCount: 0,
        avgRpm: 0,
        maxRpm: 0,
      };

    const validSpeedItems = data.filter(
      (item) =>
        typeof item.speed === "number" && !item.is_stop && item.speed > 0,
    );

    if (validSpeedItems.length === 0) {
      return {
        max: 0,
        avg: 0,
        exceedWarningCount: 0,
        exceedDangerCount: 0,
        stopCount: data.filter((item) => item.is_stop).length,
        avgRpm: 0,
        maxRpm: 0,
      };
    }

    const speeds = validSpeedItems.map((item) => item.speed as number);

    const max = Math.max(...speeds);
    const avg = speeds.reduce((sum, speed) => sum + speed, 0) / speeds.length;

    const exceedWarningCount = validSpeedItems.filter(
      (item) => (item.speed as number) > warningThreshold,
    ).length;

    const exceedDangerCount = validSpeedItems.filter(
      (item) => (item.speed as number) > dangerThreshold,
    ).length;

    const stopCount = data.filter((item) => item.is_stop).length;
    const rpms = hasCanTelemetry
      ? data
          .map((item) => Number(item.rpm))
          .filter((rpm) => Number.isFinite(rpm) && rpm > 0)
      : [];

    return {
      max,
      avg: Math.round(avg * 10) / 10,
      exceedWarningCount,
      exceedDangerCount,
      stopCount,
      avgRpm: rpms.length
        ? Math.round(
            (rpms.reduce((sum, rpm) => sum + rpm, 0) / rpms.length) * 10,
          ) / 10
        : 0,
      maxRpm: rpms.length ? Math.max(...rpms) : 0,
    };
  }, [data, warningThreshold, dangerThreshold, hasCanTelemetry]);

  const CustomTooltip: React.FC<CustomTooltipProps> = ({
    active,
    payload,
    label,
  }) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;
      const speed =
        payload.find((item) => item.dataKey === "speed")?.value ?? 0;
      const rpm = payload.find((item) => item.dataKey === "rpm")?.value;

      if (dataPoint.is_stop && dataPoint.stop_time) {
        return (
          <div className="bg-white p-3 rounded-md shadow-md border border-red-300">
            <p className="font-bold text-red-600 mb-1">PARADA</p>
            <p className="font-medium dark:text-background">{`Início: ${dataPoint.start_hour}`}</p>
            <p className="font-medium dark:text-background">{`Fim: ${dataPoint.end_hour}`}</p>
            <p className="font-medium dark:text-background">{`Duração: ${dataPoint.stop_time}`}</p>
          </div>
        );
      }

      let speedClass = "text-green-600";

      if (speed > dangerThreshold) {
        speedClass = "text-red-600 font-bold";
      } else if (speed > warningThreshold) {
        speedClass = "text-amber-600 font-semibold";
      }

      return (
        <div className="bg-white p-3 rounded-md shadow-md border border-gray-200">
          <p className="text-gray-700 mb-1">{`Hora: ${label}`}</p>
          <p className={speedClass}>
            {`Velocidade: ${speed} km/h`}
            {speed > dangerThreshold && <span className="ml-2">⚠️</span>}
          </p>
          {hasCanTelemetry && rpm !== undefined && (
            <p className="text-orange-600">RPM: {rpm}</p>
          )}
        </div>
      );
    }
    return <div />;
  };

  const CustomDot = (props: CustomDotProps) => {
    if (!props || props.payload === undefined) return null;

    const { cx = 0, cy = 0, payload } = props;

    if (payload.is_stop) {
      return (
        <circle
          cx={cx}
          cy={cy}
          r={5}
          fill="red"
          style={{ cursor: "pointer" }}
          onClick={() => handleDotClick(payload, cx, cy)}
        />
      );
    }

    return <circle cx={cx} cy={cy} r={0} fill="transparent" />;
  };

  if (!data.length) {
    return (
      <div className="flex justify-center items-center h-64 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-gray-500">Não há dados de velocidade disponíveis</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
        <h3 className="text-lg font-semibold">Análise de Velocidade</h3>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <div className="bg-blue-50 text-blue-700 px-3 py-2 rounded-md">
            <p className="text-xs text-blue-600">Velocidade Média</p>
            <p className="font-medium text-black">{statistics.avg} km/h</p>
          </div>

          <div className="bg-red-50 text-red-700 px-3 py-2 rounded-md">
            <p className="text-xs text-red-600">Velocidade Máxima</p>
            <p className="font-medium text-black">{statistics.max} km/h</p>
          </div>

          <div className="bg-amber-50 text-amber-700 px-3 py-2 rounded-md">
            <p className="text-xs text-amber-600">
              Acima de {warningThreshold}km/h
            </p>
            <p className="font-medium text-black">
              {statistics.exceedWarningCount}x
            </p>
          </div>

          <div className="bg-red-50 text-red-700 px-3 py-2 rounded-md">
            <p className="text-xs text-red-600">
              Acima de {dangerThreshold}km/h
            </p>
            <p className="font-medium text-black">
              {statistics.exceedDangerCount}x
            </p>
          </div>

          {statistics.stopCount > 0 && (
            <div className="bg-red-50 text-red-700 px-3 py-2 rounded-md">
              <p className="text-xs text-red-600">Paradas</p>
              <p className="font-medium text-black">{statistics.stopCount}</p>
            </div>
          )}

          {hasCanTelemetry && (
            <div className="bg-orange-50 text-orange-700 px-3 py-2 rounded-md">
              <p className="text-xs text-orange-600">RPM Médio</p>
              <p className="font-medium text-black">{statistics.avgRpm}</p>
            </div>
          )}
        </div>
      </div>

      <div style={{ height: `${height}px` }} className="relative">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 20, right: 10, left: 0, bottom: 60 }}
          >
            <XAxis
              dataKey="hour"
              tick={(props) => {
                const { x, y, payload } = props;
                return (
                  <g transform={`translate(${x},${y})`}>
                    <text
                      x={0}
                      y={0}
                      dy={16}
                      textAnchor="end"
                      fill="#666"
                      fontSize={12}
                      transform="rotate(-45)"
                    >
                      {payload.value}
                    </text>
                  </g>
                );
              }}
              height={60}
              interval="preserveStartEnd"
              minTickGap={15}
            />
            <YAxis
              yAxisId="speed"
              domain={[0, Math.max(dangerThreshold + 20, statistics.max + 10)]}
            />
            {hasCanTelemetry && (
              <YAxis
                yAxisId="rpm"
                orientation="right"
                domain={[0, Math.max(1000, statistics.maxRpm + 200)]}
              />
            )}
            <Tooltip content={<CustomTooltip />} />

            <ReferenceLine
              yAxisId="speed"
              y={warningThreshold}
              label={{
                value: `${warningThreshold} km/h`,
                position: "insideBottomRight",
                fill: "#f59e0b",
                fontSize: 12,
              }}
              stroke="#f59e0b"
              strokeDasharray="3 3"
            />

            <ReferenceLine
              yAxisId="speed"
              y={dangerThreshold}
              label={{
                value: `${dangerThreshold} km/h`,
                position: "insideBottomRight",
                fill: "#ef4444",
                fontSize: 12,
              }}
              stroke="#ef4444"
              strokeDasharray="3 3"
            />

            <ReferenceLine
              yAxisId="speed"
              y={statistics.avg}
              label={{
                value: `Média: ${statistics.avg} km/h`,
                position: "insideBottomLeft",
                fill: "#3b82f6",
                fontSize: 12,
              }}
              stroke="#3b82f6"
              strokeDasharray="3 3"
            />

            <Line
              yAxisId="speed"
              type="monotone"
              dataKey="speed"
              name="Velocidade"
              stroke="#3b82f6"
              strokeWidth={2}
              activeDot={<CustomActiveDot />}
              connectNulls
              dot={<CustomDot />}
            />
            {hasCanTelemetry && (
              <Line
                yAxisId="rpm"
                type="monotone"
                dataKey="rpm"
                name="RPM"
                stroke="#f97316"
                strokeWidth={2}
                connectNulls
                dot={false}
              />
            )}
          </LineChart>
        </ResponsiveContainer>

        {popup.isOpen && popup.data && (
          <div
            className="absolute bg-white p-4 rounded-md shadow-lg border border-gray-200 z-10"
            style={{
              left:
                popup.position.x > window.innerWidth / 2
                  ? popup.position.x - 200
                  : popup.position.x + 20,
              top: popup.position.y - 100,
            }}
          >
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
              onClick={closePopup}
            >
              ✕
            </button>
            <div className="mb-2">
              {popup.data.is_stop && popup.data.stop_time ? (
                <div>
                  <h4 className="font-bold text-lg text-red-600">PARADA</h4>
                  <p className="font-medium dark:text-background">{`Início: ${popup.data.start_hour}`}</p>
                  <p className="font-medium dark:text-background">{`Fim: ${popup.data.end_hour}`}</p>
                  <p className="font-medium dark:text-background">{`Duração: ${popup.data.stop_time}`}</p>
                </div>
              ) : data.indexOf(popup.data) === 0 ? (
                <div>
                  <h4 className="font-bold text-lg dark:text-background">
                    Início da Jornada
                  </h4>
                  <p className="text-gray-600">{popup.data.hour}</p>
                </div>
              ) : data.indexOf(popup.data) === data.length - 1 ? (
                <div>
                  <h4 className="font-bold text-lg dark:text-background">
                    Fim da Jornada
                  </h4>
                  <p className="text-gray-600">{popup.data.hour}</p>
                </div>
              ) : (
                <>
                  <h4 className="font-bold text-lg dark:text-background">
                    Velocidade
                  </h4>
                  <p className="text-gray-600">{`Hora: ${popup.data.hour}`}</p>
                  <p
                    className={
                      popup.data.speed && popup.data.speed > dangerThreshold
                        ? "text-red-600 font-bold"
                        : popup.data.speed &&
                            popup.data.speed > warningThreshold
                          ? "text-amber-600 font-semibold"
                          : "text-green-600"
                    }
                  >
                    {`Velocidade: ${popup.data.speed} km/h`}
                    {popup.data.speed && popup.data.speed > dangerThreshold && (
                      <span className="ml-2">⚠️</span>
                    )}
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SpeedChart;
